/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Mantine
import {
	Box,
	Group,
	Image,
	Paper,
	Skeleton,
	Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

// Styles
import classes from './import-history.module.css';

// Components
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { RowImportHistory } from '@features/sourcing/import-history/components/RowImportHistory';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';

// Apis
import { useImportHistories } from '@features/sourcing/import-history/api/get-import-histories';

// Store
import useStore from '@store/index';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Utils
import { uuid } from '@utils/functions';
import {
	IMPORT_STATUS_TRANSACTION_FILTER_OPTIONS,
	IMPORT_TYPE_OPTIONS,
} from '@utils/options';

import { Pagination } from '@components/atoms/Pagination';
import { DrawerViewDetails } from '@features/sourcing/import-history/components/DrawerViewDetails';
import { TSelectOption } from '@global-types/index';
import dayjs from 'dayjs';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const ImportHistory = () => {
	const { t } = useTranslation();
	const [isOverflowFilters] = useState(false);
	const [openDrawer, setOpenDrawer] = useState(false);

	const [showScrollFilters, setShowScrollFilters] =
		useState(false);

	const containerRef = useRef<HTMLDivElement>(null);

	const {
		setImportHistoriesPage,
		importHistoriesQueryParams,
		setImportHistoriesQueryParams,
		setImportHistoriesPageSize,
		importHistoriesPageSize,
		importHistoriesPage,
		setImportHistorySelected,
		setImportHistoriesLinesQueryParams,
	} = useStore();

	// APIs
	const {
		data: importHistories,
		isLoading,
		total: totalItems,
		totalPages,
	} = useImportHistories(
		importHistoriesPage,
		importHistoriesPageSize,
		importHistoriesQueryParams
	);

	const handleReset = () => {
		setImportHistoriesPageSize(100);
		setImportHistoriesQueryParams({
			createdAt: {
				$gte: dayjs()
					.subtract(1, 'week')
					.format('YYYY-MM-DD'),
				$lt: dayjs().add(1, 'day').format('YYYY-MM-DD'),
			},
		});
		setImportHistoriesPage(1);
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
	}, []);

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	return (
		<Box className={classes.container} ref={ref}>
			<DrawerViewDetails
				onClose={() => {
					setOpenDrawer(false);
				}}
				opened={openDrawer}
			/>
			<Paper className={classes.paper}>
				{/* Row filters */}
				<div className={classes.containerFilters}>
					<div className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<div
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={(
												mintStatusList: TSelectOption[]
											) => {
												const queryParamsMintStatus = {
													type: Array.isArray(
														mintStatusList
													)
														? mintStatusList.map(
																(
																	_mintStatus: TSelectOption
																) => _mintStatus.value
																// eslint-disable-next-line no-mixed-spaces-and-tabs
															)
														: [],
												};
												setImportHistoriesQueryParams({
													...importHistoriesQueryParams,
													...queryParamsMintStatus,
												});
											}}
											label={t('global.type')}
											size="lg"
											options={IMPORT_TYPE_OPTIONS}
											placeholderSearch={t(
												'filters.placeholderSearch.searchMintStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectMintStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={(
												mintStatusList: TSelectOption[]
											) => {
												const queryParamsMintStatus = {
													status: Array.isArray(
														mintStatusList
													)
														? mintStatusList.map(
																(
																	_mintStatus: TSelectOption
																) => _mintStatus.value
																// eslint-disable-next-line no-mixed-spaces-and-tabs
															)
														: [],
												};
												setImportHistoriesQueryParams({
													...importHistoriesQueryParams,
													...queryParamsMintStatus,
												});
											}}
											label={t('global.importStatus')}
											size="lg"
											options={
												IMPORT_STATUS_TRANSACTION_FILTER_OPTIONS
											}
											placeholderSearch={t(
												'filters.placeholderSearch.searchStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</div>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</div>
				</div>
				<Box className={classes.spacerRow} />
				<Box className={classes.containerRow}>
					<Group
						gap={'10px'}
						className={classes.containerRowLeft}
					>
						<Box
							className={classes.containerNbrSelection}
						>
							<Text
								className={classes.totalMint}
								size="xs"
							>
								{'('}
								{totalItems &&
									!isNaN(+totalItems) &&
									totalItems}
								{') '}
								{t('importHistories.importFrom7Days')}
							</Text>
						</Box>
					</Group>
				</Box>
				<Box className={classes.spacerRow} />
				{/* List mint */}
				{isLoading ? (
					<Box className={classes.containerList}>
						<Skeleton height={100} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
					</Box>
				) : (
					<Box className={classes.containerList}>
						{!isLoading &&
						importHistories &&
						Array.isArray(importHistories) &&
						importHistories.length > 0 ? (
							// eslint-disable-next-line @typescript-eslint/no-explicit-any
							importHistories.map((item: any) => (
								<RowImportHistory
									key={uuid()}
									data={item}
									verified={true}
									handleviewdetails={() => {
										setOpenDrawer(true);
										setImportHistorySelected(item);
										setImportHistoriesLinesQueryParams({
											_id: item.lines,
										});
									}}
								/>
							))
						) : (
							<Box className={classes.containerEmptyList}>
								<Box
									className={
										classes.containerTextsNoRecords
									}
								>
									<Image
										src={NoRecordsIcon}
										className={classes.iconNoRecords}
									/>
									<>
										<Text
											className={classes.titleNoRecords}
										>
											{t(
												'global.datatable.noResultsFound'
											)}
										</Text>
										<Text
											className={
												classes.descriptionNoRecords
											}
										>
											{t(
												'global.datatable.noResultsFoundDescription'
											)}
										</Text>
									</>
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Paper>
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={importHistoriesPageSize}
					totalitems={totalItems}
					activepage={importHistoriesPage}
					handlechangesize={function (
						_size: string | number
					) {
						setImportHistoriesPageSize(_size);
						setImportHistoriesPage(1);
					}}
					onChange={(data) => {
						setImportHistoriesPage(data);
					}}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default ImportHistory;
